import { Button, Paragraph, Text } from '@expressable/syntax-ds';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'react-use';
import { InsuranceDefinitionV2 } from '../misc/insuranceOptions';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';

interface Props {
  price: string;
  insuranceData: InsuranceDefinitionV2;
  isOutOfNetwork?: boolean;
  isDefaultEstimates?: boolean;
}

export const DepositDueCardBody = ({ price, insuranceData, isOutOfNetwork }: Props) => {
  const { evaluationSchedule } = useSignupDirectCtx();
  if (!evaluationSchedule) throw new Error('Expected schedule to be set');
  const evaluationType = evaluationSchedule.type;

  const isCommercialInsurance = insuranceData.insuranceType === 'Commercial';
  const [isExpanded, setIsExpanded] = useBoolean(isCommercialInsurance ? true : false);
  const { t } = useTranslation();

  const {
    expectedSessionRate,
    expectedEvaluationRate,
    averageSessionPatientResponsibility,
    averageEvalPatientResponsibility,
    isDefaultEstimates,
  } = insuranceData;
  const flags = useFlags();

  const shouldShowDepositDisclaimer = useMemo(() => {
    if (insuranceData.insuranceType === 'Commercial' && insuranceData.networkStatus === 'In-network') {
      return flags.chargeCommercialDeposits ?? false;
    }
    return false;
  }, [flags, insuranceData]);
  return (
    <div>
      <Text variant="body-sm" className="mb-8 leading-16">
        {t('directSignup.sections.insurance.labels.depositDue')}
      </Text>
      <Text variant="display">{price}</Text>
      {shouldShowDepositDisclaimer && (
          <Text variant="hint" className="mt-8 mb-8">
            {t('directSignup.eligibility.estimates.aboutDeposit')}
          </Text>
        )}

      {isExpanded && (
        <>
          <Text variant="body-sm" className="my-32 uppercase leading-16 font-semibold">
            {t('directSignup.eligibility.estimates.aboutYourEstimate')}
          </Text>
          <Paragraph variant="secondary" size="xs" noDefaultMargins>
            {isOutOfNetwork
              ? t('directSignup.eligibility.estimates.outOfNetwork')
              : t('directSignup.eligibility.estimates.estimatedCostDescription')}
          </Paragraph>
          <div className="flex flex-col gap-y-e16 gap-x-e24 md:flex-row md:justify-between mt-32">
            <div>
              <Text variant="label" className="mb-e8 block">
                {t(
                  `directSignup.eligibility.estimates.costPerEvalTitle.${
                    evaluationType === 'single' ? 'single' : 'split'
                  }`,
                )}
              </Text>
              <Text variant="body-md" className="mb-e12 text-warm-gray-800">
                {`$${averageEvalPatientResponsibility}`}
              </Text>
              {!isOutOfNetwork && (
                <>
                  {isDefaultEstimates ? (
                    <Paragraph variant="secondary" size="xs" noDefaultMargins>
                      {t('directSignup.eligibility.estimates.costPerEvalDescDefault')}
                    </Paragraph>
                  ) : (
                    <Paragraph variant="secondary" size="xs" noDefaultMargins>
                      {t('directSignup.eligibility.estimates.costPerEvalDesc', {
                        expectedRate: expectedEvaluationRate,
                        averageCost: averageEvalPatientResponsibility,
                      })}
                    </Paragraph>
                  )}
                </>
              )}
            </div>

            <div>
              <Text variant="label" className="mb-e8 block">
                {t('directSignup.eligibility.estimates.costPerSessionTitle')}
              </Text>
              <Text variant="body-md" className="mb-e12 text-warm-gray-800">
                {`$${averageSessionPatientResponsibility}`}
              </Text>
              {!isOutOfNetwork && (
                <>
                  {isDefaultEstimates ? (
                    <Paragraph variant="secondary" size="xs" noDefaultMargins>
                      {t('directSignup.eligibility.estimates.costPerSessionDescDefault')}
                    </Paragraph>
                  ) : (
                    <Paragraph variant="secondary" size="xs" noDefaultMargins>
                      {t('directSignup.eligibility.estimates.costPerSessionDesc', {
                        expectedRate: expectedSessionRate,
                        averageCost: averageSessionPatientResponsibility,
                      })}
                    </Paragraph>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {!isCommercialInsurance && (
        <Button
          variant="link"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-e4 px-0 py-0 mb-8 no-underline mt-32"
        >
          <span>View {isExpanded ? 'less' : 'more'}</span>
          {isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
        </Button>
      )}

      {!isExpanded && <div className="mb-24" />}
    </div>
  );
};
